// src/pages/xray/XrayLobby/TabARP.js
import React, { useState, useMemo } from 'react';

const TabARP = ({ selectedTabsARP, setSelectedTabsARP, arpTable, formattedArpTable }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredCell, setHoveredCell] = useState(null);
  const [copiedCell, setCopiedCell] = useState(null);

  const handleCopyToClipboard = (text, cellId) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedCell(cellId);
      setTimeout(() => setCopiedCell(null), 1500);
    });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredAndSortedArp = useMemo(() => {
    if (!formattedArpTable) {
      return formattedArpTable;
    }

    // First, filter by search term
    let filtered = formattedArpTable;
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = formattedArpTable.filter((entry) => {
        return (
          (entry.ip || '').toLowerCase().includes(lowerSearch) ||
          (entry.mac || '').toLowerCase().includes(lowerSearch) ||
          (entry.interface || '').toLowerCase().includes(lowerSearch)
        );
      });
    }

    // Then, sort if a column is selected
    if (!sortConfig.key) {
      return filtered;
    }

    const sorted = [...filtered].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';

      // Handle IP address sorting specially
      if (sortConfig.key === 'ip') {
        const aNum = aValue.split('.').map(num => num.padStart(3, '0')).join('');
        const bNum = bValue.split('.').map(num => num.padStart(3, '0')).join('');
        return sortConfig.direction === 'asc'
          ? aNum.localeCompare(bNum)
          : bNum.localeCompare(aNum);
      }

      // String comparison for other fields
      return sortConfig.direction === 'asc'
        ? aValue.toString().localeCompare(bValue.toString())
        : bValue.toString().localeCompare(aValue.toString());
    });

    return sorted;
  }, [formattedArpTable, sortConfig, searchTerm]);

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span className="ml-1 text-gray-600 text-lg">▲▼</span>;
    }
    return sortConfig.direction === 'asc' ?
      <span className="ml-1 text-lg">▲</span> :
      <span className="ml-1 text-lg">▼</span>;
  };

  return (
    <>
      <div className="tabs mt-6 flex border-b">
        {['ARP Table'].map((label, index) => (
          <label
            key={index}
            className={`cursor-pointer px-2 py-1 border-t border-r border-l border-red-600 bg-red-300 ml-1 rounded-t-md ${
              selectedTabsARP === `tabsARP${index + 1}`
                ? 'border-b-0 font-bold'
                : 'border-red-600 bg-red-600 hover:bg-red-700 transition-colors duration-200 text-white'
            }`}
          >
            <input
              type="radio"
              className="hidden"
              name="tabs-group"
              checked={selectedTabsARP === `tabsARP${index + 1}`}
              onChange={() => setSelectedTabsARP(`tabsARP${index + 1}`)}
            />
            {label}
          </label>
        ))}
      </div>

      <div className="px-6 py-2 bg-white shadow-lg rounded-lg">
        {selectedTabsARP === 'tabsARP1' && (
          <div>
            <h1 className="font-bold text-2xl p-2">ARP Table</h1>
            {formattedArpTable?.length ? (
              <>
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by IP, MAC, or Interface..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                  {searchTerm && (
                    <p className="text-sm text-gray-600 mt-2">
                      Found {filteredAndSortedArp.length} of {formattedArpTable.length} entries
                    </p>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full table-auto border-collapse border border-red-300 text-sm">
                    <thead>
                      <tr className="bg-red-400 text-left">
                        <th
                          className="border border-black px-2 py-1 cursor-pointer hover:bg-red-500 select-none"
                          onClick={() => handleSort('ip')}
                        >
                          IP<SortIcon columnKey="ip" />
                        </th>
                        <th
                          className="border border-black px-2 py-1 cursor-pointer hover:bg-red-500 select-none"
                          onClick={() => handleSort('mac')}
                        >
                          MAC<SortIcon columnKey="mac" />
                        </th>
                        <th className="border border-black px-2 py-1 bg-red-400">
                          MAC no colon
                        </th>
                        <th
                          className="border border-black px-2 py-1 cursor-pointer hover:bg-red-500 select-none"
                          onClick={() => handleSort('interface')}
                        >
                          Interface<SortIcon columnKey="interface" />
                        </th>
                        <th className="border border-black px-2 py-1 bg-red-400">
                          Connectivity test
                        </th>
                        <th className="border border-black px-2 py-1 bg-red-400">
                          TCPDUMP
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAndSortedArp.map((entry, index) => {
                        const macNoColon = entry.mac ? entry.mac.replace(/:/g, '') : '';
                        return (
                          <tr key={index} className="hover:bg-red-200">
                            <td
                              className="border border-black px-2 py-1 relative"
                              onMouseEnter={() => setHoveredCell(`${index}-ip`)}
                              onMouseLeave={() => setHoveredCell(null)}
                            >
                              {entry.ip}
                              {hoveredCell === `${index}-ip` && (
                                <button
                                  onClick={() => handleCopyToClipboard(entry.ip, `${index}-ip`)}
                                  className="absolute right-1 top-1/2 -translate-y-1/2 hover:opacity-70 p-0.5"
                                  title="Copy to clipboard"
                                >
                                  {copiedCell === `${index}-ip` ? (
                                    <span className="text-green-600 text-sm font-bold">✓</span>
                                  ) : (
                                    <img src="/img/svg-copy-to-clipboard.svg" alt="Copy" className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                            </td>
                            <td
                              className="border border-black px-2 py-1 relative"
                              onMouseEnter={() => setHoveredCell(`${index}-mac`)}
                              onMouseLeave={() => setHoveredCell(null)}
                            >
                              {entry.mac}
                              {hoveredCell === `${index}-mac` && (
                                <button
                                  onClick={() => handleCopyToClipboard(entry.mac, `${index}-mac`)}
                                  className="absolute right-1 top-1/2 -translate-y-1/2 hover:opacity-70 p-0.5"
                                  title="Copy to clipboard"
                                >
                                  {copiedCell === `${index}-mac` ? (
                                    <span className="text-green-600 text-sm font-bold">✓</span>
                                  ) : (
                                    <img src="/img/svg-copy-to-clipboard.svg" alt="Copy" className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                            </td>
                            <td
                              className="border border-black px-2 py-1 relative"
                              onMouseEnter={() => setHoveredCell(`${index}-macNoColon`)}
                              onMouseLeave={() => setHoveredCell(null)}
                            >
                              {macNoColon}
                              {hoveredCell === `${index}-macNoColon` && (
                                <button
                                  onClick={() => handleCopyToClipboard(macNoColon, `${index}-macNoColon`)}
                                  className="absolute right-1 top-1/2 -translate-y-1/2 hover:opacity-70 p-0.5"
                                  title="Copy to clipboard"
                                >
                                  {copiedCell === `${index}-macNoColon` ? (
                                    <span className="text-green-600 text-sm font-bold">✓</span>
                                  ) : (
                                    <img src="/img/svg-copy-to-clipboard.svg" alt="Copy" className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                            </td>
                            <td
                              className="border border-black px-2 py-1 relative"
                              onMouseEnter={() => setHoveredCell(`${index}-interface`)}
                              onMouseLeave={() => setHoveredCell(null)}
                            >
                              {entry.interface}
                              {hoveredCell === `${index}-interface` && (
                                <button
                                  onClick={() => handleCopyToClipboard(entry.interface, `${index}-interface`)}
                                  className="absolute right-1 top-1/2 -translate-y-1/2 hover:opacity-70 p-0.5"
                                  title="Copy to clipboard"
                                >
                                  {copiedCell === `${index}-interface` ? (
                                    <span className="text-green-600 text-sm font-bold">✓</span>
                                  ) : (
                                    <img src="/img/svg-copy-to-clipboard.svg" alt="Copy" className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                            </td>
                            <td
                              className="border border-black px-2 py-1 relative"
                              onMouseEnter={() => setHoveredCell(`${index}-ping`)}
                              onMouseLeave={() => setHoveredCell(null)}
                            >
                              {`ping ${entry.ip}`}
                              {hoveredCell === `${index}-ping` && (
                                <button
                                  onClick={() => handleCopyToClipboard(`ping ${entry.ip}`, `${index}-ping`)}
                                  className="absolute right-1 top-1/2 -translate-y-1/2 hover:opacity-70 p-0.5"
                                  title="Copy to clipboard"
                                >
                                  {copiedCell === `${index}-ping` ? (
                                    <span className="text-green-600 text-sm font-bold">✓</span>
                                  ) : (
                                    <img src="/img/svg-copy-to-clipboard.svg" alt="Copy" className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                            </td>
                            <td
                              className="border border-black px-2 py-1 relative"
                              onMouseEnter={() => setHoveredCell(`${index}-tcpdump`)}
                              onMouseLeave={() => setHoveredCell(null)}
                            >
                              {`tcpdump -s0 -i ${entry.interface} -vv host ${entry.ip}`}
                              {hoveredCell === `${index}-tcpdump` && (
                                <button
                                  onClick={() => handleCopyToClipboard(`tcpdump -s0 -i ${entry.interface} -vv host ${entry.ip}`, `${index}-tcpdump`)}
                                  className="absolute right-1 top-1/2 -translate-y-1/2 hover:opacity-70 p-0.5"
                                  title="Copy to clipboard"
                                >
                                  {copiedCell === `${index}-tcpdump` ? (
                                    <span className="text-green-600 text-sm font-bold">✓</span>
                                  ) : (
                                    <img src="/img/svg-copy-to-clipboard.svg" alt="Copy" className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <pre className="bg-gray-100 p-4 rounded-md border">
                {arpTable || 'No ARP Table available'}
              </pre>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default TabARP;
